<template>
  <div>
    <!-- Compact checklist: only when admin, incomplete, and not dismissed -->
    <v-card outlined class="mb-4 vms-checklist-card" v-if="showChecklistCard">
      <v-card-title class="info--text text-h6 py-3">
        <v-icon color="info" class="mr-2">mdi-clipboard-check-outline</v-icon>
        VMS Setup Checklist
        <v-spacer></v-spacer>
        <v-chip small :color="checklist.ready ? 'success' : 'warning'" dark class="mr-2">
          {{ checklist.completed }}/{{ checklist.total }} ready
        </v-chip>
        <v-btn icon small title="Dismiss" @click="dismissChecklist">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="pb-0">
        Optional setup guide. Dismiss anytime — reopen from Help.
      </v-card-subtitle>
      <v-card-text>
        <v-list dense>
          <v-list-item v-for="item in checklist.items" :key="item.key" @click="$router.push(item.route)">
            <v-list-item-icon>
              <v-icon :color="item.done ? 'success' : 'grey'">
                {{ item.done ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.label }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn text small color="info" class="text-capitalize">Open</v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div class="d-flex flex-wrap align-center">
          <v-btn text color="info" class="text-capitalize" to="/help">
            <v-icon left small>mdi-school-outline</v-icon>
            Open full guide
          </v-btn>
          <v-btn text color="grey" class="text-capitalize" @click="openTourManually">
            Quick start tour
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text small class="text-capitalize" @click="dismissChecklist">Don't show again</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <div v-else-if="isAdmin && checklistDismissed && !checklist.ready" class="mb-3">
      <v-btn text small color="info" class="text-capitalize px-0" @click="restoreChecklist">
        <v-icon left small>mdi-clipboard-check-outline</v-icon>
        Show VMS setup checklist
      </v-btn>
    </div>

    <v-dialog v-model="showTour" max-width="680" persistent @keydown.esc="dismissTour">
      <v-card>
        <v-card-title class="d-flex align-center">
          Quick start: Setup → Invite → Approve
          <v-spacer></v-spacer>
          <v-btn icon small @click="dismissTour">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-stepper v-model="tourStep" alt-labels>
            <v-stepper-header>
              <v-stepper-step :complete="tourStep > 1" step="1">Setup groups</v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="tourStep > 2" step="2">Setup workflows</v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="tourStep > 3" step="3">Invite guest</v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step step="4">Approve</v-stepper-step>
            </v-stepper-header>
            <v-stepper-items>
              <v-stepper-content step="1">
                <p class="mb-2"><strong>Setup — Approval groups</strong></p>
                <ol class="mb-3 pl-4">
                  <li>Open <strong>Approvals → Groups</strong>.</li>
                  <li>Create one <strong>branch</strong> group per location and add that branch’s approvers.</li>
                  <li>Optionally create an <strong>HQ / company-wide</strong> group for people who can approve at any branch.</li>
                </ol>
                <v-btn color="info" class="text-capitalize mr-2" to="/approval-groups" @click="goAndKeepTour">Open Groups</v-btn>
              </v-stepper-content>
              <v-stepper-content step="2">
                <p class="mb-2"><strong>Setup — Workflows</strong></p>
                <ol class="mb-3 pl-4">
                  <li>Open <strong>Approvals → Workflows</strong>.</li>
                  <li>Create one <strong>HQ</strong> workflow and one shared <strong>Branch</strong> workflow.</li>
                  <li>Attach the groups from step 1. Keep minimum approvers at <strong>1</strong>.</li>
                </ol>
                <v-btn color="info" class="text-capitalize mr-2" to="/workflows" @click="goAndKeepTour">Open Workflows</v-btn>
              </v-stepper-content>
              <v-stepper-content step="3">
                <p class="mb-2"><strong>Invite a guest</strong></p>
                <ol class="mb-3 pl-4">
                  <li>Go to <strong>Organizations</strong>, open a branch, then <strong>Schedule Visit</strong>.</li>
                  <li>Click <strong>Invite visitor</strong>.</li>
                  <li>Enter the guest. Location and workflow autofill. Save.</li>
                  <li>Approvers are emailed. The guest gets SMS only after final approval.</li>
                </ol>
                <v-btn color="info" class="text-capitalize mr-2" to="/organisations" @click="goAndKeepTour">Open Organizations</v-btn>
              </v-stepper-content>
              <v-stepper-content step="4">
                <p class="mb-2"><strong>Approve the visit</strong></p>
                <ol class="mb-3 pl-4">
                  <li>Open the email link or <strong>Today’s Visits</strong>.</li>
                  <li>Open the visit. Approve / Reject show only if it is your group’s turn.</li>
                  <li>On final approval: Host + Security emailed; visitor SMS sent.</li>
                  <li>On rejection: Host is emailed with the reason.</li>
                </ol>
                <p class="caption grey--text mb-0">Tip: use the “Needs my approval” filter on Today’s Visits.</p>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
        <v-card-actions>
          <v-btn text class="text-capitalize" @click="dismissTour">Skip</v-btn>
          <v-btn text class="text-capitalize" to="/help" @click="dismissTour">Full guide</v-btn>
          <v-spacer></v-spacer>
          <v-btn text :disabled="tourStep <= 1" @click="tourStep--">Back</v-btn>
          <v-btn color="info" dark class="text-capitalize" @click="nextStep">
            {{ tourStep >= 4 ? 'Done' : 'Next' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import VmsSetupService from "../services/vms_setup.service";

const TOUR_SEEN_KEY = "vms_tour_seen";
const CHECKLIST_DISMISSED_KEY = "vms_checklist_dismissed";

export default {
  name: "VmsSetupChecklist",
  props: {
    organizationId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    checklist: {
      items: [],
      completed: 0,
      total: 0,
      ready: false,
    },
    showTour: false,
    tourStep: 1,
    checklistDismissed: false,
  }),
  computed: {
    user() {
      return JSON.parse(localStorage.getItem("user") || "{}");
    },
    isAdmin() {
      return Number(this.user?.is_admin) === 1;
    },
    showChecklistCard() {
      return this.isAdmin && !this.checklistDismissed && !this.checklist.ready;
    },
  },
  created() {
    if (!this.isAdmin) return;

    this.checklistDismissed = localStorage.getItem(CHECKLIST_DISMISSED_KEY) === "1";
    this.loadChecklist();
    this.showTour = false;
  },
  methods: {
    loadChecklist() {
      VmsSetupService.checklist(this.organizationId).then(
        (response) => {
          this.checklist = response?.data?.data || this.checklist;
          if (this.checklist.ready) {
            localStorage.setItem(CHECKLIST_DISMISSED_KEY, "1");
            this.checklistDismissed = true;
          }
        },
        () => {}
      );
    },
    dismissTour() {
      localStorage.setItem(TOUR_SEEN_KEY, "1");
      this.showTour = false;
    },
    goAndKeepTour() {
      // Navigate via router-link; mark tour seen so it does not block
      localStorage.setItem(TOUR_SEEN_KEY, "1");
      this.showTour = false;
    },
    openTourManually() {
      this.tourStep = 1;
      this.showTour = true;
    },
    nextStep() {
      if (this.tourStep >= 4) {
        this.dismissTour();
        return;
      }
      this.tourStep += 1;
    },
    dismissChecklist() {
      localStorage.setItem(CHECKLIST_DISMISSED_KEY, "1");
      this.checklistDismissed = true;
    },
    restoreChecklist() {
      localStorage.removeItem(CHECKLIST_DISMISSED_KEY);
      this.checklistDismissed = false;
      this.loadChecklist();
    },
  },
};
</script>
